import { useEffect, useState } from "react";

// Remembers the Inbound/Outbound search bar's typed values (search-by toggle, provider/patient
// name, date range) across tab switches and refreshes, mirroring the sessionStorage pattern
// ContextApi.js already uses for sideBarLinkActive/activeTab. Read once at module scope so every
// field's initial useState can pull from the same parsed blob instead of re-reading/parsing
// sessionStorage five times on mount.
const CONSOLE_SEARCH_STORAGE_KEY = "cm_consoleSearch";
const readStoredConsoleSearch = () => {
  try {
    const stored = sessionStorage.getItem(CONSOLE_SEARCH_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (_) {
    return {};
  }
};
const storedConsoleSearch = readStoredConsoleSearch();

// localDateKey: "YYYY-MM-DD" from the browser's own local date parts, instead of
// toISOString() (which reports the UTC date - a call placed in the evening can roll over to
// "tomorrow" hours before local midnight depending on the viewer's UTC offset). Matches the
// convention Dashboard.jsx's own localDateKey/getDefaultStartDate already use for these fields.
const localDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Month-to-date: 1st of the current calendar month through today, in the viewer's own local
// time. Recomputed fresh from `referenceDate` every call (never persisted) so a tab left open
// across a midnight or month boundary still lands on "today" on next mount instead of replaying
// a stale range. Exported as a pure function so its boundary behavior (month rollover, etc.) can
// be unit tested without mounting the hook.
export const getMonthToDateRange = (referenceDate = new Date()) => {
  const monthStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  return {
    startDate: localDateKey(monthStart),
    endDate: localDateKey(referenceDate),
  };
};

// Initial date range: if a real, user-picked range was persisted (hasExplicitDateRange: true in
// storage), restore it as-is. Otherwise - including a brand-new session, or one where the range
// was still sitting at its auto-computed default when the tab closed - compute a fresh
// month-to-date range right now rather than trusting anything persisted, since an unbounded
// (empty) or stale default is exactly what made Inbound/Outbound's first load slow.
const initialDateRange = storedConsoleSearch.hasExplicitDateRange
  ? {
      startDate: storedConsoleSearch.startDate || "",
      endDate: storedConsoleSearch.endDate || "",
      hasExplicitDateRange: true,
    }
  : { ...getMonthToDateRange(), hasExplicitDateRange: false };

export const useConsoleSearch = () => {
  const [searchActive, setSearchActive] = useState(
    storedConsoleSearch.searchActive || "patient",
  );
  const [providerName, setProviderName] = useState(storedConsoleSearch.providerName || "");
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  // Distinguishes "the picker is showing our auto-computed month-to-date default, nobody has
  // touched it yet" (false) from "the user explicitly picked/cleared a range on one of the tabs
  // sharing this state" (true). Consumers that need to preserve their own pre-existing
  // empty-range default behavior (e.g. Summary's 90-day backend lookback) branch on this instead
  // of on startDate/endDate directly, since those are now always populated.
  const [hasExplicitDateRange, setHasExplicitDateRange] = useState(
    initialDateRange.hasExplicitDateRange,
  );
  const [patientName, setPatientName] = useState(storedConsoleSearch.patientName || "");

  useEffect(() => {
    sessionStorage.setItem(
      CONSOLE_SEARCH_STORAGE_KEY,
      JSON.stringify({
        searchActive,
        providerName,
        // Only ever persist a range once the user has made it explicit - never the
        // auto-computed month-to-date default - so a session resumed later recomputes a fresh
        // "today" instead of replaying whatever month-to-date range happened to be showing when
        // the tab closed.
        startDate: hasExplicitDateRange ? startDate : "",
        endDate: hasExplicitDateRange ? endDate : "",
        hasExplicitDateRange,
        patientName,
      }),
    );
  }, [searchActive, providerName, startDate, endDate, hasExplicitDateRange, patientName]);

  return {
    searchActive,
    setSearchActive,
    providerName,
    setProviderName,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    hasExplicitDateRange,
    setHasExplicitDateRange,
    patientName,
    setPatientName,
  };
};
